import { useState, useEffect, useCallback } from 'react';
import { ServiceProviderType, Category, MasterDataManageRequest, MasterDataManageResponse } from '../models/ServiceProviderType';
import { masterDataService } from '../services/masterDataService';

interface UseMasterDataReturn {
  serviceProviderTypes: ServiceProviderType[];
  categories: Category[];
  loading: boolean;
  error: string | null;
  selectedType: string | null;
  fetchServiceProviderTypes: () => Promise<void>;
  fetchCategories: (tag: string) => Promise<void>;
  setSelectedType: (tag: string | null) => void;
  manageMasterData: (request: MasterDataManageRequest) => Promise<MasterDataManageResponse>;
}

export const useMasterData = (): UseMasterDataReturn => {
  const [serviceProviderTypes, setServiceProviderTypes] = useState<ServiceProviderType[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const fetchServiceProviderTypes = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await masterDataService.getServiceProviderTypes();
      if (response.success) {
        setServiceProviderTypes(response.data.serviceProviderTypes);
      } else {
        setError('Failed to fetch service provider types');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching service provider types');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async (tag: string): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await masterDataService.getCategoriesByType(tag);
      if (response.success) {
        setCategories(response.data.categories);
      } else {
        setError('Failed to fetch categories');
      }
    } catch (err) {
      console.error('❌ useMasterData: Error fetching categories:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while fetching categories');
    } finally {
      setLoading(false);
    }
  }, []);

  const manageMasterData = async (request: MasterDataManageRequest): Promise<MasterDataManageResponse> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await masterDataService.manageMasterData(request);
      
      // Refresh data after successful operation
      if (response.success) {
        await fetchServiceProviderTypes();
        if (selectedType) {
          await fetchCategories(selectedType);
        }
      }
      
      return response;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while managing master data');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Fetch service provider types on mount
  useEffect(() => {
    fetchServiceProviderTypes();
  }, [fetchServiceProviderTypes]);

  // Fetch categories when selected type changes
  useEffect(() => {
    if (selectedType) {
      fetchCategories(selectedType);
    } else {
      setCategories([]);
    }
  }, [selectedType, fetchCategories]);

  return {
    serviceProviderTypes,
    categories,
    loading,
    error,
    selectedType,
    fetchServiceProviderTypes,
    fetchCategories,
    setSelectedType,
    manageMasterData,
  };
};
